import { Box, Button, Paper, Tooltip, Typography } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { useEffect, useRef, useState } from "react";

type TargetAudienceProps = {
  hasFilters: boolean;
  appliedChips: string[];
  setShowFilter: (v: boolean) => void;
};

const StepCircle = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      bgcolor: brand[600],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ color: "white", fontWeight: 600, fontSize: 14 }}>
      {number}
    </Typography>
  </Box>
);

const FilterChip = ({ text }: { text: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowed, setOverflowed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setOverflowed(el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <Tooltip
      title={overflowed ? text : ""}
      disableHoverListener={!overflowed}
      disableFocusListener={!overflowed}
      disableTouchListener={!overflowed}
    >
      <Box
        ref={ref}
        sx={{
          display: "inline-block",
          verticalAlign: "middle",
          px: 1.5,
          py: 0.5,
          border: "1px solid #B2DDFF",
          borderRadius: "20px",
          bgcolor: "#EFF8FF",
          fontSize: 13,
          color: "#175CD3",
          maxWidth: 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
};

export const TargetAudience = ({
  hasFilters,
  appliedChips,
  setShowFilter,
}: TargetAudienceProps) => {
  return (
    <Paper sx={{ p: 3, borderRadius: "12px" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <StepCircle number={2} />
          <Typography variant="h6" fontWeight={600}>
            Target audience
          </Typography>
          <Typography variant="body2" color="text.secondary">
            (Who this rule applies to)
          </Typography>
        </Box>
        <Button
          id="rule-add-filter-button"
          variant="outlined"
          startIcon={<AddIcon sx={{ color: "#344054" }} />}
          onClick={() => setShowFilter(true)}
          sx={{
            borderRadius: "12px",
            borderColor: gray[300],
            color: "#344054",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
          }}
        >
          Add Filter
        </Button>
      </Box>

      {!hasFilters ? (
        <Box
          sx={{
            border: "1px dashed #D0D5DD",
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#FFFFFF",
              borderRadius: "50%",
              border: "1px solid #F3F4F6",
              width: 40,
              height: 40,
              boxShadow: "0px 1px 2px 0px #1018280D",
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 20, color: "#98A2B3" }} />
          </Box>
          <Typography fontSize={16} fontWeight={600} color="#475467">
            No filters applied.
          </Typography>
          <Typography color="#475467" textAlign="center" fontSize={14}>
            This rule currently matches all users. Use the &ldquo;Add
            filter&rdquo; button to define who this rule applies to
          </Typography>
        </Box>
      ) : (
        <Box
          id="rule-edit-filter-button"
          onClick={() => setShowFilter(true)}
          sx={{
            border: "1px dashed #D0D5DD",
            borderRadius: "8px",
            backgroundColor: "#F9FAFB",
            p: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            cursor: "pointer",
            "&:hover": { borderColor: gray[400], backgroundColor: gray[50] },
          }}
        >
          {appliedChips.map((chip, i) => (
            <FilterChip key={i} text={chip} />
          ))}
        </Box>
      )}
    </Paper>
  );
};
